import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  TextField,
  Button,
  Grid,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  InputAdornment,
  FormControlLabel,
  Switch,
  Checkbox,
  FormGroup,
  FormLabel,
  RadioGroup,
  Radio,
  Divider,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  Schedule as TimeIcon,
  SwapVert as SwapIcon,
  DirectionsCar as CarIcon,
  Settings as SettingsIcon,
  Accessible as AccessibleIcon,
  SmokeFree as SmokingIcon,
  Pets as PetsIcon,
  Usb as UsbIcon,
  DirectionsBike as BikeIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLoading } from '../../contexts/LoadingContext';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeContext } from '../../contexts/ThemeContext';
import apiService from '../../services/apiService';
import { CAR_MAKES, CAR_MODELS, VEHICLE_TYPES, VEHICLE_COLORS } from '../../data/vehicleData';
import { Chip } from '@mui/material';
import LocationAutocomplete, { LocationData } from '../../components/Common/LocationAutocomplete';
import SocialShareModal from '../../components/Common/SocialShareModal';

const LUGGAGE_OPTIONS = [
  { value: 'none', label: 'No luggage allowed', icon: '🚫' },
  { value: 'small', label: 'Small bags only', icon: '👜' },
  { value: 'medium', label: 'Medium suitcases', icon: '🎒' },
  { value: 'large', label: 'Large suitcases', icon: '🧳' },
  { value: 'backpack', label: 'Backpacks welcome', icon: '🎒' },
];

const CURRENT_YEAR = new Date().getFullYear();

// Custom day component for DateTimePicker with bold and 50% larger font
const CustomPickersDay = (props: any) => {
  return (
    <PickersDay
      {...props}
      sx={{
        fontWeight: 'bold !important',
        fontSize: '1.5em !important', // 50% increase from default 1em
        ...props.sx
      }}
    />
  );
};

// interface City removed, using LocationData from LocationAutocomplete

interface VehicleMake {
  id: string;
  name: string;
}

interface VehicleModel {
  id: string;
  name: string;
  makeId: string;
  type: string;
  defaultSeats: number;
}

const CreateRidePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setLoading } = useLoading();
  const { user, refreshUserData } = useAuth();
  const { actualMode: mode } = useThemeContext();

  // Stripe Connect Express State & Handlers
  const [verifyingPayout, setVerifyingPayout] = useState(false);
  const [payoutCheckError, setPayoutCheckError] = useState('');
  const [payoutCheckSuccess, setPayoutCheckSuccess] = useState('');

  const handleVerifyPayout = async () => {
    setVerifyingPayout(true);
    setPayoutCheckError('');
    setPayoutCheckSuccess('');
    try {
      const response = await apiService.post('/payments/connect/status', {});
      if (response.success && response.data.stripeConnectOnboarded) {
        setPayoutCheckSuccess('Bank account linked successfully! You can now offer rides with credit card payment.');
        await refreshUserData();
      } else {
        setPayoutCheckError('Bank account is not linked yet. Please complete Stripe onboarding in the other tab.');
      }
    } catch (err: any) {
      console.error('Error verifying payout status:', err);
      setPayoutCheckError(err.message || 'Failed to verify payout status. Please try again.');
    } finally {
      setVerifyingPayout(false);
    }
  };

  const handleLinkPayout = async () => {
    setVerifyingPayout(true);
    setPayoutCheckError('');
    setPayoutCheckSuccess('');
    try {
      const response = await apiService.post('/payments/connect/onboard', {});
      if (response.success && response.data.url) {
        window.open(response.data.url, '_blank');
      } else {
        setPayoutCheckError('Failed to generate Stripe onboarding link.');
      }
    } catch (err: any) {
      console.error('Error opening Stripe onboarding link:', err);
      setPayoutCheckError(err.message || 'Failed to open Stripe onboarding portal.');
    } finally {
      setVerifyingPayout(false);
    }
  };

  // Check driver status
  useEffect(() => {
    if (!user) return;

    if (!user.isDriver || user.driverStatus === 'NOT_APPLIED') {
      navigate('/become-driver');
    } else if (user.driverStatus === 'PENDING') {
      // Redirect back - application under review
      alert('Your driver application is under review. You cannot create rides yet.');
      navigate('/my-rides');
    } else if (user.driverStatus === 'REJECTED') {
      alert('Your driver application was rejected. Please contact support.');
      navigate('/my-rides');
    } else if (user.driverStatus === 'SUSPENDED') {
      alert('Your driver account is suspended. Please contact support.');
      navigate('/my-rides');
    }
    // AI_VERIFIED and APPROVED users can create rides - no redirect needed
  }, [user, navigate]);

  const [userVehicles, setUserVehicles] = useState<any[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<any>(null);


  const [formData, setFormData] = useState({
    // Route
    originCity: null as LocationData | null,
    destinationCity: null as LocationData | null,
    pickupInstructions: '',
    dropoffInstructions: '',

    // Schedule
    departureDateTime: new Date(),
    returnDateTime: new Date(new Date().getTime() + 24 * 60 * 60 * 1000),
    availableSeats: 1,
    pricePerSeat: '',

    // Vehicle Information
    vehicleMakeId: '',
    vehicleMake: '',
    vehicleModelId: '',
    vehicleModel: '',
    vehicleYear: CURRENT_YEAR,
    vehicleType: '',
    vehicleSeats: 4,
    vehicleColor: '',
    licensePlate: '',
    vehicleFeatures: [] as string[],

    // Trip Conditions
    allowsSmoking: false,
    allowsPets: false,
    allowsWheelchair: false,
    allowsBikes: false,
    luggagePolicy: 'medium' as string,
    manualApprovalRequired: false,
    allowChat: true, // Allow drivers to ask questions about this ride

    // Additional Features
    hasUSBCharger: false,
    hasWiFi: false,
    hasAC: false,
    hasHeating: false,

    // Notes
    description: '',
    paymentMethod: 'CASH' as string,
  });

  // originCityOptions and destinationCityOptions states, limits, and searches are managed internally by LocationAutocomplete 
  const [returnTripEnabled, setReturnTripEnabled] = useState(false);
  const [recurringEnabled, setRecurringEnabled] = useState(false);
  const [recurringDates, setRecurringDates] = useState<Date[]>([]);
  const [shareItem, setShareItem] = useState<{ type: 'ride' | 'request'; data: any } | null>(null);

  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingRideId, setEditingRideId] = useState<string | null>(null);

  // Load user's vehicles on mount and handle duplicate data
  useEffect(() => {
    const loadData = async () => {
      try {
        const vehiclesRes = await apiService.get('/vehicles/my-vehicles');

        if (vehiclesRes.success) {
          setUserVehicles(vehiclesRes.data.vehicles);
        }

        // Check if we're editing a ride
        if (location.state?.editRide) {
          const edit = location.state.editRide;
          setEditingRideId(edit.id);

          // Find the vehicle if it exists
          const vehicle = vehiclesRes.data.vehicles?.find((v: any) => v.id === edit.vehicleId);
          if (vehicle) {
            setSelectedVehicle(vehicle);
          }

          // Load cities if provided
          let originCityCache: any = null;
          if (edit.originCityId) {
            try {
              const originRes = await apiService.get(`/locations/cities/${edit.originCityId}`);
              if (originRes.success) {
                originCityCache = originRes.data.city;
              }
            } catch (err) {
              console.error('Failed to load origin city:', err);
            }
          }

          let destCityCache: any = null;
          if (edit.destinationCityId) {
            try {
              const destRes = await apiService.get(`/locations/cities/${edit.destinationCityId}`);
              if (destRes.success) {
                destCityCache = destRes.data.city;
              }
            } catch (err) {
              console.error('Failed to load destination city:', err);
            }
          }

          // Prefill all fields including date/time
          setFormData(prev => ({
            ...prev,
            originCity: originCityCache ? { id: originCityCache.id, name: originCityCache.name, province: originCityCache.province, latitude: originCityCache.latitude, longitude: originCityCache.longitude } : null,
            destinationCity: destCityCache ? { id: destCityCache.id, name: destCityCache.name, province: destCityCache.province, latitude: destCityCache.latitude, longitude: destCityCache.longitude } : null,
            departureDateTime: new Date(edit.departureDateTime),
            returnDateTime: edit.returnDateTime ? new Date(edit.returnDateTime) : new Date(new Date(edit.departureDateTime).getTime() + 24 * 60 * 60 * 1000),
            pickupInstructions: edit.originDetails || '',
            dropoffInstructions: edit.destinationDetails || '',
            availableSeats: edit.totalSeats || 1,
            pricePerSeat: edit.pricePerPerson?.toString() || '',
            allowsSmoking: edit.allowsSmoking || false,
            allowsPets: edit.allowsPets || false,
            allowsBikes: edit.allowsLargeLuggage || false,
            description: edit.additionalNotes || '',
            allowChat: edit.allowChat !== undefined ? edit.allowChat : true,
            paymentMethod: edit.paymentMethod || 'CASH',
          }));
        }
        // Check if we're duplicating a ride
        else if (location.state?.duplicateFrom) {
          const dup = location.state.duplicateFrom;

          // Find the vehicle if it exists
          const vehicle = vehiclesRes.data.vehicles?.find((v: any) => v.id === dup.vehicleId);
          if (vehicle) {
            setSelectedVehicle(vehicle);
          }

          // Load cities if provided
          let originCityCache: any = null;
          if (dup.originCityId) {
            try {
              const originRes = await apiService.get(`/locations/cities/${dup.originCityId}`);
              if (originRes.success) {
                originCityCache = originRes.data.city;
              }
            } catch (err) {
              console.error('Failed to load origin city:', err);
            }
          }

          let destCityCache: any = null;
          if (dup.destinationCityId) {
            try {
              const destRes = await apiService.get(`/locations/cities/${dup.destinationCityId}`);
              if (destRes.success) {
                destCityCache = destRes.data.city;
              }
            } catch (err) {
              console.error('Failed to load destination city:', err);
            }
          }

          // Prefill other fields
          setFormData(prev => ({
            ...prev,
            originCity: originCityCache ? { id: originCityCache.id, name: originCityCache.name, province: originCityCache.province, latitude: originCityCache.latitude, longitude: originCityCache.longitude } : null,
            destinationCity: destCityCache ? { id: destCityCache.id, name: destCityCache.name, province: destCityCache.province, latitude: destCityCache.latitude, longitude: destCityCache.longitude } : null,
            pickupInstructions: dup.originDetails || '',
            dropoffInstructions: dup.destinationDetails || '',
            availableSeats: dup.totalSeats || 1,
            pricePerSeat: dup.pricePerPerson?.toString() || '',
            returnDateTime: dup.returnDateTime ? new Date(dup.returnDateTime) : prev.returnDateTime,
            allowsSmoking: dup.allowsSmoking || false,
            allowsPets: dup.allowsPets || false,
            allowsBikes: dup.allowsLargeLuggage || false,
            description: dup.additionalNotes || '',
            allowChat: dup.allowChat !== undefined ? dup.allowChat : true,
            paymentMethod: dup.paymentMethod || 'CASH',
          }));
        }
      } catch (error) {
        console.error('Load data error:', error);
      }
    };

    loadData();
  }, [location.state]);

  const handleSwapCities = () => {
    setFormData(prev => ({
      ...prev,
      originCity: prev.destinationCity,
      destinationCity: prev.originCity,
      pickupInstructions: prev.dropoffInstructions,
      dropoffInstructions: prev.pickupInstructions,
    }));
  };

  const toggleRecurringDate = (date: Date | null) => {
    if (!date) return;
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    setRecurringDates((prev) => {
      const exists = prev.some((d) => d.getTime() === normalized.getTime());
      if (exists) {
        return prev.filter((d) => d.getTime() !== normalized.getTime());
      }
      return [...prev, normalized].sort((a, b) => a.getTime() - b.getTime());
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.originCity || !formData.destinationCity) {
      setError('Please select both origin and destination cities');
      return;
    }

    if (formData.originCity.id === formData.destinationCity.id) {
      setError('Origin and destination must be different cities');
      return;
    }

    if (!formData.pricePerSeat || parseFloat(formData.pricePerSeat) <= 0) {
      setError('Please enter a valid price per seat');
      return;
    }

    if (formData.departureDateTime <= new Date()) {
      setError('Departure time must be in the future');
      return;
    }

    if (returnTripEnabled && formData.returnDateTime <= formData.departureDateTime) {
      setError('Return time must be after the departure time');
      return;
    }

    if (formData.paymentMethod === 'CREDIT_CARD' && user && !user.stripeConnectOnboarded) {
      setError('You must link your bank account via Stripe Connect to offer credit card payment rides. Please link your bank account using the warning card below.');
      return;
    }

    // Vehicle validation
    if (userVehicles.length === 0) {
      setError('You must add and verify a vehicle in Vehicle Management before you can offer a ride.');
      return;
    }

    if (!selectedVehicle) {
      setError('Please select a vehicle from your list');
      return;
    }

    setIsSubmitting(true);
    setLoading(true, 'Creating your ride...');

    try {
      const vehicleId = selectedVehicle?.id;

      // Ensure we have a vehicleId
      if (!vehicleId) {
        setError('Please select a vehicle from your list.');
        setIsSubmitting(false);
        setLoading(false);
        return;
      }

      const buildDepartureDateTime = (date: Date) => {
        const base = new Date(formData.departureDateTime);
        const combined = new Date(date);
        combined.setHours(base.getHours(), base.getMinutes(), 0, 0);
        return combined;
      };

      const rideDataBase = {
        vehicleId,
        originCityId: formData.originCity!.id,
        originLat: formData.originCity!.latitude,
        originLng: formData.originCity!.longitude,
        originName: formData.originCity!.name,
        originProvince: formData.originCity!.province,

        destinationCityId: formData.destinationCity!.id,
        destLat: formData.destinationCity!.latitude,
        destLng: formData.destinationCity!.longitude,
        destName: formData.destinationCity!.name,
        destProvince: formData.destinationCity!.province,
        originDetails: formData.pickupInstructions,
        destinationDetails: formData.dropoffInstructions,
        totalSeats: formData.availableSeats,
        pricePerPerson: parseFloat(formData.pricePerSeat),
        allowsSmoking: formData.allowsSmoking,
        allowsPets: formData.allowsPets,
        allowsLargeLuggage: formData.allowsBikes, // Map allowsBikes to allowsLargeLuggage for now
        additionalNotes: formData.description,
        manualApprovalRequired: formData.manualApprovalRequired,
        allowChat: formData.allowChat,
        tripType: returnTripEnabled ? 'RETURN' : 'SINGLE',
        paymentMethod: formData.paymentMethod,
        ...(returnTripEnabled ? { returnDateTime: formData.returnDateTime.toISOString() } : {})
      };

      const primaryDate = buildDepartureDateTime(formData.departureDateTime);
      const extraDates = recurringEnabled
        ? recurringDates
          .filter((d) => d.getTime() !== new Date(formData.departureDateTime).setHours(0, 0, 0, 0))
          .map(buildDepartureDateTime)
        : [];
      const allDates = [primaryDate, ...extraDates];

      const createRideForDate = async (dt: Date) => {
        const rideData = { ...rideDataBase, departureDateTime: dt.toISOString() };
        return apiService.post('/rides', rideData);
      };

      let rideRes;
      if (editingRideId) {
        // Update existing ride (single)
        rideRes = await apiService.put(`/rides/${editingRideId}`, {
          ...rideDataBase,
          departureDateTime: primaryDate.toISOString()
        });
        if (!rideRes.success) {
          throw new Error(rideRes.message || 'Failed to update ride');
        }
      } else {
        // Create one or many rides for selected dates
        for (const dt of allDates) {
          rideRes = await createRideForDate(dt);
          if (!rideRes.success) {
            throw new Error(rideRes.message || 'Failed to create ride');
          }
        }
      }

      const rideId = rideRes?.data?.ride?.id || editingRideId;

      setShareItem({
        type: 'ride',
        data: {
          id: rideId,
          originCity: formData.originCity,
          destinationCity: formData.destinationCity,
          departureDateTime: primaryDate,
          isEdit: !!editingRideId
        }
      });
    } catch (error: any) {
      console.error('Create ride error:', error);
      // Extract the proper error message from the API response
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create ride. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ flexGrow: 1, p: { xs: 1, md: 3 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 'bold',
              mb: { xs: 2, md: 4 },
              fontSize: { xs: '1.5rem', md: '2rem' },
              textAlign: { xs: 'center', md: 'left' }
            }}
          >
             {editingRideId ? '✏️ Edit Ride' : '🚗 Offer a Ride'}
          </Typography>

          <Paper
            elevation={3}
            sx={{
              p: { xs: 2, md: 4 },
              borderRadius: 3,
            }}
          >
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Route Section */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon />
                    Route Details
                  </Typography>
                </Grid>

                {/* From Field */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <LocationAutocomplete
                    label="From"
                    placeholder="e.g. Toronto, ON"
                    icon={<LocationIcon sx={{ color: '#ef4444', fontSize: '1.5rem' }} />}
                    value={formData.originCity}
                    onChange={(newValue: any) => setFormData({ ...formData, originCity: newValue })}
                    modeColor="#2563eb"
                  />
                  <TextField
                    fullWidth
                    label="Pickup Instructions"
                    value={formData.pickupInstructions}
                    onChange={(e) =>
                      setFormData({ ...formData, pickupInstructions: e.target.value })
                    }
                    placeholder="Specific pickup location, meeting point, etc."
                    multiline
                    rows={2}
                    sx={{ mt: 2 }}
                  />
                </Grid>

                {/* Swap Button */}
                <Grid size={{ xs: 12, md: 12 }} sx={{ display: 'flex', justifyContent: 'center', my: { xs: 1, md: 0 } }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleSwapCities}
                    sx={{
                      minWidth: 48,
                      height: 48,
                      borderRadius: '50%',
                      p: 0,
                      borderColor: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.light',
                        borderColor: 'primary.main',
                      },
                    }}
                    title="Swap From and To"
                  >
                    <SwapIcon />
                  </Button>
                </Grid>

                {/* To Field */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <LocationAutocomplete
                    label="To"
                    placeholder="e.g. Montreal, QC"
                    icon={<LocationIcon sx={{ color: '#8b5cf6', fontSize: '1.5rem' }} />}
                    value={formData.destinationCity}
                    onChange={(newValue: any) => setFormData({ ...formData, destinationCity: newValue })}
                    modeColor="#2563eb"
                  />
                  <TextField
                    fullWidth
                    label="Drop-off Instructions"
                    value={formData.dropoffInstructions}
                    onChange={(e) =>
                      setFormData({ ...formData, dropoffInstructions: e.target.value })
                    }
                    placeholder="Specific drop-off location, meeting point, etc."
                    multiline
                    rows={2}
                    sx={{ mt: 2 }}
                  />
                </Grid>

                {/* Date and Time */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TimeIcon />
                    Departure Details
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <DateTimePicker
                    label="Departure Date & Time"
                    value={formData.departureDateTime}
                    onChange={(newValue) =>
                      newValue && setFormData({ ...formData, departureDateTime: newValue })
                    }
                    minDateTime={new Date()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                      },
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={returnTripEnabled}
                        onChange={(e) => setReturnTripEnabled(e.target.checked)}
                      />
                    }
                    label="Add return trip"
                  />
                </Grid>

                {returnTripEnabled && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <DateTimePicker
                      label="Return Date & Time"
                      value={formData.returnDateTime}
                      onChange={(newValue) =>
                        newValue && setFormData({ ...formData, returnDateTime: newValue })
                      }
                      minDateTime={formData.departureDateTime}
                      slots={{
                        day: CustomPickersDay
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                        },
                      }}
                    />
                  </Grid>
                )}

                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={recurringEnabled}
                        onChange={(e) => setRecurringEnabled(e.target.checked)}
                      />
                    }
                    label="Recurring trip (select additional dates)"
                  />
                  {recurringEnabled && (
                    <Box sx={{ mt: 2 }}>
                      <DateCalendar
                        value={null}
                        onChange={(date) => toggleRecurringDate(date)}
                        disablePast
                        slots={{
                          day: (dayProps) => {
                            const dayDate = new Date(dayProps.day);
                            dayDate.setHours(0, 0, 0, 0);
                            const isSelected = recurringDates.some(
                              (d) => d.getTime() === dayDate.getTime()
                            );
                            return (
                              <PickersDay
                                {...dayProps}
                                selected={isSelected}
                                sx={{
                                  ...(isSelected && {
                                    bgcolor: 'text.primary !important',
                                    color: 'background.paper !important',
                                    fontWeight: 'bold',
                                    '&:hover': {
                                      bgcolor: 'text.secondary !important',
                                    },
                                    '&.Mui-selected': {
                                      bgcolor: 'text.primary !important',
                                      color: 'background.paper !important',
                                    },
                                  }),
                                }}
                              />
                            );
                          },
                        }}
                      />
                      {recurringDates.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                          {recurringDates.map((d) => (
                            <Chip
                              key={d.toISOString()}
                              label={d.toLocaleDateString()}
                              onDelete={() => toggleRecurringDate(d)}
                            />
                          ))}
                        </Box>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        Selected dates will create additional rides with the same time and details.
                      </Typography>
                    </Box>
                  )}
                </Grid>

                {/* Pricing */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MoneyIcon />
                    Pricing
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Price per Seat"
                    value={formData.pricePerSeat}
                    onChange={(e) =>
                      setFormData({ ...formData, pricePerSeat: e.target.value })
                    }
                    required
                    type="number"
                    inputProps={{ min: 1, step: 0.01 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl component="fieldset" required>
                    <FormLabel component="legend" sx={{ mb: 1 }}>Payment Method</FormLabel>
                    <RadioGroup
                      row
                      value={formData.paymentMethod}
                      onChange={(e) =>
                        setFormData({ ...formData, paymentMethod: e.target.value })
                      }
                    >
                      <FormControlLabel
                        value="CASH"
                        control={<Radio />}
                        label="Cash"
                      />
                      <FormControlLabel
                        value="CREDIT_CARD"
                        control={<Radio />}
                        label="Credit Card (Stripe)"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                {/* Real-time fee and tax calculator */}
                {formData.pricePerSeat && parseFloat(formData.pricePerSeat) > 0 && (
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{
                      mt: 2,
                      p: 2.5,
                      bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.015)',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'
                    }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5, color: 'text.primary', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MoneyIcon sx={{ fontSize: 20 }} />
                        Estimated Earnings Breakdown (per seat)
                      </Typography>
                      {formData.paymentMethod === 'CREDIT_CARD' ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Passenger Price:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>${parseFloat(formData.pricePerSeat).toFixed(2)}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Stripe Processing Fee (2.9% + $0.30):</Typography>
                            <Typography variant="body2" color="error.main" sx={{ fontWeight: 'medium' }}>
                              -${((parseFloat(formData.pricePerSeat) * 0.029) + 0.30).toFixed(2)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px dashed', borderColor: 'divider', pt: 1, mt: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Your Estimated Net Payout:</Typography>
                            <Typography variant="body2" color="success.main" sx={{ fontWeight: 'bold' }}>
                              ${Math.max(0, parseFloat(formData.pricePerSeat) - ((parseFloat(formData.pricePerSeat) * 0.029) + 0.30)).toFixed(2)}
                            </Typography>
                          </Box>
                          <Typography variant="body1" color="text.secondary" sx={{ display: 'block', mt: 1.5, fontStyle: 'italic', fontSize: '1.15rem', lineHeight: 1.4 }}>
                            * Ride Club charges 0% commission. The transaction fee is charged directly by Stripe. Payout clearing takes 3 business days.
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Passenger Pays:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>${parseFloat(formData.pricePerSeat).toFixed(2)}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px dashed', borderColor: 'divider', pt: 1, mt: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Your Net Payout (Direct Cash):</Typography>
                            <Typography variant="body2" color="success.main" sx={{ fontWeight: 'bold' }}>
                              ${parseFloat(formData.pricePerSeat).toFixed(2)}
                            </Typography>
                          </Box>
                          <Typography variant="body1" color="text.secondary" sx={{ display: 'block', mt: 1.5, fontStyle: 'italic', fontSize: '1.15rem', lineHeight: 1.4 }}>
                            * No transaction fees apply. Passenger pays you directly in cash at pickup/dropoff.
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1.5, mt: 1.5 }}>
                        <Typography variant="body1" color="warning.main" sx={{ fontWeight: 'bold', display: 'block', fontSize: '1.25rem', lineHeight: 1.5 }}>
                          ⚠️ Tax Notice: You are responsible for filing and reporting the money you collect from ridesharing. Ride Club does not provide or issue tax documents or T4 forms for the CRA.
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                {formData.paymentMethod === 'CREDIT_CARD' && user && !user.stripeConnectOnboarded && (
                  <Grid size={{ xs: 12 }}>
                    <Alert 
                      severity="warning" 
                      sx={{ 
                        mt: 2, 
                        p: 2,
                        borderRadius: '16px',
                        border: '1px solid #f59e0b',
                        bgcolor: '#fffbeb',
                        '& .MuiAlert-message': { width: '100%' }
                      }}
                    >
                      <Typography sx={{ fontWeight: 'bold', fontSize: '1.25rem', mb: 0.5, color: '#b45309' }}>
                        ⚠️ Payout Setup Required
                      </Typography>
                      <Typography sx={{ fontSize: '1.1rem', mb: 2, color: '#78350f' }}>
                        To receive passenger credit card fare payouts directly to your bank account, you must link your bank account via Stripe Connect Express.
                      </Typography>
                      
                      {payoutCheckError && (
                        <Typography sx={{ color: 'error.main', fontSize: '1.1rem', mb: 2, fontWeight: 600 }}>
                          ❌ {payoutCheckError}
                        </Typography>
                      )}
                      {payoutCheckSuccess && (
                        <Typography sx={{ color: 'success.main', fontSize: '1.1rem', mb: 2, fontWeight: 600 }}>
                          ✅ {payoutCheckSuccess}
                        </Typography>
                      )}

                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Button 
                          variant="contained" 
                          color="warning" 
                          onClick={handleLinkPayout}
                          disabled={verifyingPayout}
                          sx={{ textTransform: 'none', fontSize: '1.1rem', py: 1, px: 3, borderRadius: '8px' }}
                        >
                          Link Bank Account
                        </Button>
                        <Button 
                          variant="outlined" 
                          color="warning" 
                          onClick={handleVerifyPayout}
                          disabled={verifyingPayout}
                          sx={{ textTransform: 'none', fontSize: '1.1rem', py: 1, px: 3, borderRadius: '8px', bgcolor: 'white' }}
                        >
                          {verifyingPayout ? 'Verifying...' : 'Verify Setup'}
                        </Button>
                      </Box>
                    </Alert>
                  </Grid>
                )}

                {/* Vehicle Information */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CarIcon />
                    Vehicle Information
                  </Typography>
                </Grid>                {/* Info message when no vehicles */}
                {userVehicles.length === 0 && (
                  <Grid size={{ xs: 12 }}>
                    <Alert 
                      severity="warning" 
                      sx={{ 
                        mt: 2, 
                        p: 2,
                        borderRadius: '16px',
                        border: '1px solid #f59e0b',
                        bgcolor: '#fffbeb',
                        '& .MuiAlert-message': { width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }
                      }}
                    >
                      <Typography sx={{ fontWeight: 'bold', fontSize: '1.25rem', mb: 0.5, color: '#b45309' }}>
                        ⚠️ No Vehicles Registered
                      </Typography>
                      <Typography sx={{ fontSize: '1.1rem', color: '#78350f', mb: 1 }}>
                        You must add and verify a vehicle in Vehicle Management before you can offer a ride.
                      </Typography>
                      <Button
                        variant="contained"
                        color="warning"
                        onClick={() => navigate('/vehicles', { state: { showAddVehicleModal: true } })}
                        sx={{ textTransform: 'none', fontSize: '1.1rem', py: 1, px: 3, borderRadius: '8px', width: 'fit-content' }}
                      >
                        Go to Vehicle Management
                      </Button>
                    </Alert>
                  </Grid>
                )}

                {userVehicles.length > 0 && (
                  <>
                    {/* Select from existing vehicles */}
                    <Grid size={{ xs: 12 }}>
                      <FormControl fullWidth required>
                        <InputLabel>Select Vehicle</InputLabel>
                        <Select
                          value={selectedVehicle?.id || ''}
                          label="Select Vehicle"
                          onChange={(e) => {
                            const vehicle = userVehicles.find(v => v.id === e.target.value);
                            setSelectedVehicle(vehicle);
                            if (vehicle) {
                              setFormData({
                                ...formData,
                                vehicleMake: vehicle.make?.name || vehicle.make,
                                vehicleModel: vehicle.model?.name || vehicle.model,
                                vehicleYear: vehicle.year,
                                vehicleType: vehicle.type,
                                vehicleColor: vehicle.color,
                                licensePlate: vehicle.licensePlate,
                                vehicleFeatures: vehicle.features || [],
                                availableSeats: Math.max(1, vehicle.seats - 1) // Driver takes 1 seat
                              });
                            }
                          }}
                        >
                          {userVehicles.map((vehicle) => (
                            <MenuItem key={vehicle.id} value={vehicle.id}>
                              {vehicle.year} {vehicle.make?.name || vehicle.make} {vehicle.model?.name || vehicle.model} - {vehicle.color} ({vehicle.licensePlate})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-start' }}>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => navigate('/vehicles', { state: { showAddVehicleModal: true } })}
                          startIcon={<CarIcon />}
                          sx={{ textTransform: 'none', borderRadius: '8px', py: 0.5, px: 1.5, fontSize: '0.95rem' }}
                        >
                          Register a New Vehicle
                        </Button>
                      </Box>
                    </Grid>

                    {/* Seats (depends on vehicle selection) */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <FormControl fullWidth required>
                        <InputLabel>Available Seats</InputLabel>
                        <Select
                          value={formData.availableSeats}
                          label="Available Seats"
                          onChange={(e) =>
                            setFormData({ ...formData, availableSeats: e.target.value as number })
                          }
                        >
                          {(() => {
                            const baseSeats = selectedVehicle ? selectedVehicle.seats : 4;
                            const maxSeats = Math.max(1, baseSeats - 1); // driver occupies one seat
                            const seatOptions = [];
                            for (let i = 1; i <= maxSeats; i++) {
                              seatOptions.push(
                                <MenuItem key={i} value={i}>
                                  {i} seat{i > 1 ? 's' : ''}
                                </MenuItem>
                              );
                            }
                            return seatOptions;
                          })()}
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}

                {/* Vehicle Features */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SettingsIcon />
                    Vehicle Features
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <FormGroup>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.hasUSBCharger}
                            onChange={(e) =>
                              setFormData({ ...formData, hasUSBCharger: e.target.checked })
                            }
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <UsbIcon fontSize="small" />
                            USB Charger
                          </Box>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.hasWiFi}
                            onChange={(e) =>
                              setFormData({ ...formData, hasWiFi: e.target.checked })
                            }
                          />
                        }
                        label="WiFi"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.hasAC}
                            onChange={(e) =>
                              setFormData({ ...formData, hasAC: e.target.checked })
                            }
                          />
                        }
                        label="Air Conditioning"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.hasHeating}
                            onChange={(e) =>
                              setFormData({ ...formData, hasHeating: e.target.checked })
                            }
                          />
                        }
                        label="Heating"
                      />
                    </Box>
                  </FormGroup>
                </Grid>

                {/* Ride Preferences */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SettingsIcon />
                    Ride Preferences
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Luggage Policy</FormLabel>
                    <RadioGroup
                      value={formData.luggagePolicy}
                      onChange={(e) =>
                        setFormData({ ...formData, luggagePolicy: e.target.value })
                      }
                    >
                      {LUGGAGE_OPTIONS.map((option) => (
                        <FormControlLabel
                          key={option.value}
                          value={option.value}
                          control={<Radio />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>{option.icon}</span>
                              {option.label}
                            </Box>
                          }
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <FormGroup>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>Allowed in Vehicle</Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.allowsSmoking}
                          onChange={(e) =>
                            setFormData({ ...formData, allowsSmoking: e.target.checked })
                          }
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SmokingIcon fontSize="small" />
                          Smoking
                        </Box>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.allowsPets}
                          onChange={(e) =>
                            setFormData({ ...formData, allowsPets: e.target.checked })
                          }
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PetsIcon fontSize="small" />
                          Pets
                        </Box>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.allowsWheelchair}
                          onChange={(e) =>
                            setFormData({ ...formData, allowsWheelchair: e.target.checked })
                          }
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AccessibleIcon fontSize="small" />
                          Wheelchair Accessible
                        </Box>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.allowsBikes}
                          onChange={(e) =>
                            setFormData({ ...formData, allowsBikes: e.target.checked })
                          }
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BikeIcon fontSize="small" />
                          Bikes Allowed
                        </Box>
                      }
                    />
                  </FormGroup>
                </Grid>

                {/* Manual Approval Toggle */}
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 2 }} />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.manualApprovalRequired}
                        onChange={(e) =>
                          setFormData({ ...formData, manualApprovalRequired: e.target.checked })
                        }
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          Approve passengers manually
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          When enabled, you will need to approve each join request. When disabled, join requests are automatically approved.
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>

                {/* Allow Chat Toggle */}
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 2 }} />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.allowChat}
                        onChange={(e) =>
                          setFormData({ ...formData, allowChat: e.target.checked })
                        }
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          Allow drivers to ask questions about this ride
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          When enabled, drivers can send you messages about this ride. When disabled, messaging is not allowed.
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>

                {/* Additional Notes */}
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 2 }} />
                  <TextField
                    fullWidth
                    label="Additional Notes"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder="Any additional information for passengers..."
                    multiline
                    rows={3}
                  />
                </Grid>

                {/* Submit Buttons */}
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/my-rides')}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting || userVehicles.length === 0}
                      sx={{ minWidth: 120 }}
                    >
                      {isSubmitting ? 'Offering...' : 'Offer the Ride'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Container>
      </Box>



      {shareItem && (
        <SocialShareModal
          open={!!shareItem}
          onClose={() => {
            const isEdit = shareItem.data?.isEdit;
            setShareItem(null);
            navigate('/my-rides', {
              state: {
                message: isEdit ? 'Ride updated successfully!' : 'Ride created successfully!',
                type: 'success'
              }
            });
          }}
          type={shareItem.type}
          data={shareItem.data}
          showSuccess={true}
        />
      )}
    </LocalizationProvider>
  );
};

export default CreateRidePage;